import { Check, ChevronDown, Gauge } from 'lucide-react';

import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { cn } from '@apps/web/lib/utils';

export type AdminAgentTier =
    | 'auto'
    | 'instant'
    | 'medium'
    | 'high'
    | 'extra_high'
    | 'pro';

export type ExplicitAdminAgentTier = Exclude<AdminAgentTier, 'auto'>;

type AdminAgentTierOption = {
    readonly value: AdminAgentTier;
    readonly label: string;
    readonly description: string;
};

const DEFAULT_ADMIN_AGENT_TIER_OPTION: AdminAgentTierOption = {
    value: 'auto',
    label: 'Auto',
    description: 'Rules-based routing',
};

const ADMIN_AGENT_TIER_OPTIONS: ReadonlyArray<AdminAgentTierOption> = [
    DEFAULT_ADMIN_AGENT_TIER_OPTION,
    {
        value: 'instant',
        label: 'Instant',
        description: 'Fast lookup',
    },
    {
        value: 'medium',
        label: 'Medium',
        description: 'Single trace or target',
    },
    {
        value: 'high',
        label: 'High',
        description: 'Diagnostic investigation',
    },
    {
        value: 'extra_high',
        label: 'Extra High',
        description: 'Broad trace batches',
    },
    {
        value: 'pro',
        label: 'Pro',
        description: 'Deep fan-out',
    },
];

const TIER_LABEL_BY_VALUE = new Map(
    ADMIN_AGENT_TIER_OPTIONS.map((option) => [option.value, option.label]),
);

export function adminAgentTierLabel(
    tier: AdminAgentTier | ExplicitAdminAgentTier | null | undefined,
): string | null {
    if (!tier) {
        return null;
    }
    return TIER_LABEL_BY_VALUE.get(tier) ?? null;
}

export function isAdminAgentTier(value: string): value is AdminAgentTier {
    return ADMIN_AGENT_TIER_OPTIONS.some((option) => option.value === value);
}

export function AdminAgentTierPicker({
    selectedTier,
    resolvedTier,
    resolutionReason,
    disabled = false,
    onTierChange,
}: {
    readonly selectedTier: AdminAgentTier;
    readonly resolvedTier?: ExplicitAdminAgentTier | null;
    readonly resolutionReason?: string | null;
    readonly disabled?: boolean;
    readonly onTierChange: (tier: AdminAgentTier) => void;
}) {
    const selectedOption =
        ADMIN_AGENT_TIER_OPTIONS.find(
            (option) => option.value === selectedTier,
        ) ?? DEFAULT_ADMIN_AGENT_TIER_OPTION;
    const resolvedLabel = adminAgentTierLabel(resolvedTier);
    const triggerLabel =
        selectedTier === 'auto' && resolvedLabel
            ? `auto → ${resolvedLabel.toLowerCase()}`
            : selectedOption.label.toLowerCase();

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    className="aa-chip hover:aa-ink h-[22px] max-w-full gap-1.5 bg-transparent px-2.5 font-normal hover:border-[var(--aa-border-strong)] hover:bg-transparent"
                    title={resolutionReason ?? undefined}
                >
                    <Gauge className="size-3 shrink-0" />
                    <span className="truncate">{triggerLabel}</span>
                    <ChevronDown className="size-3 shrink-0 opacity-70" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64 p-2">
                {/* Portaled to <body>: page-scoped aa-* utilities do not
                    reach here, so the label is styled inline. */}
                <div className="text-muted-foreground px-2 py-2 font-mono text-[11px] font-medium tracking-[0.05em] uppercase">
                    Thinking mode
                </div>
                {ADMIN_AGENT_TIER_OPTIONS.map((option) => (
                    <DropdownMenuItem
                        key={option.value}
                        onSelect={() => onTierChange(option.value)}
                        className="items-start gap-2"
                    >
                        <Gauge
                            className={cn(
                                'mt-0.5 size-3.5 shrink-0',
                                selectedTier === option.value
                                    ? 'text-foreground'
                                    : 'text-muted-foreground',
                            )}
                        />
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm leading-5">
                                {option.label}
                            </span>
                            <span className="text-muted-foreground block text-xs leading-4">
                                {option.description}
                            </span>
                        </span>
                        {selectedTier === option.value ? (
                            <Check className="mt-0.5 size-4 shrink-0" />
                        ) : null}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
